/**
 * Bosqich yo'li -- surib o'zgartiriladigan bar (foydalanuvchi talabi, 2026-10-17): 8 segment, o'tilganlari qora, hozirgisi
 * sariq, dastak sudraladi yoki bosqich nomi bosiladi. Ruxsat etilgan o'tishlar `allowedTargets` dan, qolganlari xira.
 * Amal server tasdig'i bilan: `pending` -- optimistik holat (dastak darhol ko'chadi), rad etilsa orqaga qaytadi.
 */
import { useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { LOST_STATUSES, PIPELINE_ORDER, type DriverStatus } from "@/features/drivers/types";
import { barTargets, stageIndexAt } from "@/features/drivers/stageBar";

export function StageBar({
  status, reason, days, staleDays, allowed, pending, onPick,
}: {
  status: DriverStatus;
  reason: string;
  days: number;
  staleDays: number;
  allowed: DriverStatus[];
  pending: DriverStatus | null;
  onPick: (to: DriverStatus) => void;
}) {
  const { t } = useTranslation("drivers");
  const { t: tc } = useTranslation();
  const trackRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<{ x: number; idx: number } | null>(null);
  const [preview, setPreview] = useState<number | null>(null); // klaviatura yoki nom ustida turganda nishon
  const n = PIPELINE_ORDER.length;
  const shown = pending ?? status;
  const lost = LOST_STATUSES.includes(shown);
  const current = lost ? -1 : PIPELINE_ORDER.indexOf(shown);
  const targets = barTargets(status, allowed);
  const target = drag ? drag.idx : preview;
  const canDrop = (i: number) => i !== current && targets.has(PIPELINE_ORDER[i]);

  function pointAt(e: PointerEvent): { x: number; idx: number } | null {
    const el = trackRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    const x = Math.min(Math.max(e.clientX - r.left, 0), r.width);
    return { x, idx: stageIndexAt(x, r.width, n) };
  }
  function onHandleDown(e: PointerEvent<HTMLButtonElement>) {
    if (pending || targets.size === 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = pointAt(e);
    if (p) setDrag(p);
  }
  function onHandleMove(e: PointerEvent<HTMLButtonElement>) {
    if (!drag) return;
    const p = pointAt(e);
    if (p) setDrag(p);
  }
  function onHandleUp(e: PointerEvent<HTMLButtonElement>) {
    if (!drag) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    const idx = drag.idx;
    setDrag(null);
    if (canDrop(idx)) onPick(PIPELINE_ORDER[idx]);
  }
  function onKey(e: KeyboardEvent<HTMLButtonElement>) {
    const base = preview ?? Math.max(current, 0);
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      setPreview(Math.min(Math.max(base + (e.key === "ArrowRight" ? 1 : -1), 0), n - 1));
    } else if ((e.key === "Enter" || e.key === " ") && preview !== null) {
      e.preventDefault();
      if (canDrop(preview)) onPick(PIPELINE_ORDER[preview]);
      setPreview(null);
    } else if (e.key === "Escape") {
      setPreview(null);
    }
  }
  const handleLeft = drag ? `${drag.x}px` : `${((Math.max(current, 0) + 0.5) / n) * 100}%`;
  const tipIdx = target !== null && target !== current ? target : null;

  return (
    <section aria-label={t("profile.stageBar.label")} className="my-5 px-1">
      {lost && (
        <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-status-rejected px-3 py-1 text-[12px] font-semibold">
          {tc(`status.${status}`)}
          {reason && <span className="font-normal text-ink-soft">· {reason}</span>}
        </div>
      )}
      <div className="relative py-3">
        <div
          ref={trackRef}
          className="flex h-2.5 touch-none gap-[3px]"
          onPointerDown={(e) => {
            // Segmentni bosish = o'sha bosqichga o'tish (dastak alohida element, bu yerga kelmaydi)
            if (pending || drag) return;
            const p = pointAt(e);
            if (p && canDrop(p.idx)) onPick(PIPELINE_ORDER[p.idx]);
          }}
        >
          {PIPELINE_ORDER.map((s, i) => {
            const done = i < current;
            const active = i === current;
            const ok = canDrop(i);
            const isTarget = tipIdx === i;
            return (
              <span
                key={s}
                className={cn(
                  "h-full flex-1 rounded-full transition-colors duration-150",
                  done ? "bg-ink" : active ? "bg-accent" : "bg-fill",
                  isTarget && ok && "bg-accent/45",
                  isTarget && !ok && "bg-danger-soft",
                  !active && (ok ? "cursor-pointer" : "cursor-not-allowed"),
                )}
              />
            );
          })}
        </div>
        {!lost && (
          <button
            type="button"
            role="slider"
            aria-valuemin={0}
            aria-valuemax={n - 1}
            aria-valuenow={Math.max(current, 0)}
            aria-valuetext={tc(`status.${shown}`)}
            aria-label={t("profile.stageBar.hint")}
            title={targets.size === 0 ? undefined : t("profile.stageBar.hint")}
            disabled={!!pending || targets.size === 0}
            onPointerDown={onHandleDown}
            onPointerMove={onHandleMove}
            onPointerUp={onHandleUp}
            onPointerCancel={() => setDrag(null)}
            onKeyDown={onKey}
            onBlur={() => setPreview(null)}
            style={{ left: handleLeft }}
            className={cn(
              "absolute top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 touch-none rounded-full bg-accent shadow-accent ring-[3px] ring-surface outline-none focus-visible:ring-accent/40",
              drag ? "scale-110 cursor-grabbing" : "cursor-grab transition-[left,transform] duration-200",
              pending && "animate-pulse",
              targets.size === 0 && "cursor-default",
            )}
          >
            {tipIdx !== null && (
              <span
                className={cn(
                  "pointer-events-none absolute bottom-full left-1/2 mb-2 -translate-x-1/2 rounded-lg px-2.5 py-1 text-[12px] font-semibold whitespace-nowrap shadow-menu",
                  canDrop(tipIdx) ? "bg-ink text-surface" : "bg-danger-soft text-danger",
                )}
              >
                {canDrop(tipIdx) ? `→ ${tc(`status.${PIPELINE_ORDER[tipIdx]}`)}` : t("profile.stageBar.notAllowed")}
              </span>
            )}
          </button>
        )}
      </div>
      <ol className="flex">
        {PIPELINE_ORDER.map((s, i) => {
          const done = i < current;
          const active = i === current;
          const ok = canDrop(i);
          const isTarget = tipIdx === i && ok;
          return (
            <li key={s} className="flex min-w-0 flex-1 flex-col items-start gap-0.5 pr-2">
              <button
                type="button"
                disabled={!ok}
                onClick={() => onPick(s)}
                onMouseEnter={() => !drag && ok && setPreview(i)}
                onMouseLeave={() => !drag && setPreview(null)}
                className={cn(
                  "max-w-full truncate text-left text-[12px] transition-colors",
                  active ? "font-semibold text-ink" : done ? "text-ink-soft" : ok ? "text-muted hover:text-ink" : "text-faint",
                  isTarget && "text-ink underline decoration-accent decoration-2 underline-offset-4",
                  ok && "cursor-pointer",
                )}
              >
                {tc(`status.${s}`)}
              </button>
              {active && s !== "hired" && (
                <span className={cn("text-[11px]", pending ? "text-muted" : days >= staleDays ? "font-semibold text-danger" : "text-muted")}>
                  {pending ? t("profile.stageBar.saving") : t("profile.daysInStage", { count: days })}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
